import Big from "big.js";
import { bigMath } from "../misc";
import {
	OrderInputLike,
	OrderItemInputLike,
	OrderItemOptionInputLike,
} from "../types";
import { $deepPopulateItemMetadata, $isOrderUnpaid } from "./_internals";
import { calculateOrder } from "./calculate-order";

export const applyVatToOrder = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	order,
	vat,
}: {
	order: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>;
	vat: number;
}) => {
	let orderItems = order.items.map($deepPopulateItemMetadata);
	const subtotal = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal),
		0,
	);

	orderItems = orderItems.map((orderItem) => {
		const isIncluded =
			!!orderItem.original_price_addons.vat.percentage &&
			!!orderItem.original_price_addons.vat.amount;
		const vatPercentage = isIncluded
			? orderItem.original_price_addons.vat.percentage
			: orderItem.no_vat
				? 0
				: vat;

		const grossSales = bigMath.sub(
			orderItem.subtotal,
			orderItem.subtotal_addons.offer.amount,
			orderItem.subtotal_addons.discount.amount,
		);
		const addedVatScRate = bigMath.add(
			vatPercentage,
			orderItem.original_price_addons.service_charge.percentage,
		);
		const vatAmount = new Big(
			new Big(
				new Big(grossSales).sub(
					new Big(grossSales).div(new Big(1).add(addedVatScRate)),
				),
			).mul(vatPercentage),
		)
			.div(addedVatScRate || 1)
			.round(2)
			.toNumber();

		const subtotal_addons = {
			...orderItem.subtotal_addons,
			vat: {
				is_included: isIncluded,
				percentage: vatPercentage,
				amount: vatAmount,
			},
		};

		return { ...orderItem, subtotal_addons };
	});

	// options only stats
	orderItems = orderItems.map((orderItem) => ({
		...orderItem,
		options: orderItem.options.map((orderItemOption) => {
			const isIncluded =
				!!orderItemOption.original_price_addons.vat.percentage &&
				!!orderItemOption.original_price_addons.vat.amount;
			const vatPercentage = isIncluded
				? orderItemOption.original_price_addons.vat.percentage
				: orderItem.no_vat
					? 0
					: vat;

			const grossSales = bigMath.sub(
				orderItemOption.subtotal,
				orderItemOption.subtotal_addons.offer.amount,
				orderItemOption.subtotal_addons.discount.amount,
			);
			const addedVatScRate = bigMath.add(
				vatPercentage,
				orderItemOption.original_price_addons.service_charge.percentage,
			);
			const vatAmount = new Big(
				new Big(
					new Big(grossSales).sub(
						new Big(grossSales).div(new Big(1).add(addedVatScRate)),
					),
				).mul(vatPercentage),
			)
				.div(addedVatScRate || 1)
				.round(2)
				.toNumber();

			return {
				...orderItemOption,
				subtotal_addons: {
					...orderItemOption.subtotal_addons,
					vat: {
						is_included: isIncluded,
						percentage: vatPercentage,
						amount: vatAmount,
					},
				},
			};
		}),
	}));

	const vat_amount = orderItems.reduce(
		(pre, cur) => bigMath.add(pre, cur.subtotal_addons.vat.amount),
		0,
	);

	return calculateOrder({ ...order, items: orderItems, subtotal, vat_amount })
		.$order;
};

export const applyVatToOrderGroup = <
	TOrder extends OrderInputLike = OrderInputLike,
	TOrderItem extends OrderItemInputLike = OrderItemInputLike,
	TOrderItemOption extends OrderItemOptionInputLike = OrderItemOptionInputLike,
>({
	orders,
	vat,
	skipPaidCheck = false,
}: {
	orders: OrderInputLike<TOrder, TOrderItem, TOrderItemOption>[];
	vat: number;
	skipPaidCheck?: boolean;
}) => {
	return orders
		.filter((o) => (skipPaidCheck ? true : $isOrderUnpaid(o)))
		.map((order) => applyVatToOrder({ order, vat }));
};
